/* ==========================================================================
   Require Profile
   Task F9/F19: Route wrapper with slug-based redirect
   ========================================================================== */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, useProfileComplete } from '@/hooks/useAuth'
import { usePortalSlug } from '@/context/PortalContext'

interface RequireProfileProps {
  /** The protected content to render when profile is complete */
  children: React.ReactNode
}

/**
 * Route wrapper that ensures the user has a complete profile.
 * Must be used inside a ProtectedRoute (requires authentication).
 * Redirects to profile setup page if profile is incomplete.
 *
 * @example
 * ```tsx
 * <Route
 *   path="/prescription"
 *   element={
 *     <ProtectedRoute>
 *       <RequireProfile>
 *         <PrescriptionForm />
 *       </RequireProfile>
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export function RequireProfile({ children }: RequireProfileProps) {
  const { loading } = useAuth()
  const profileComplete = useProfileComplete()
  const location = useLocation()
  const slug = usePortalSlug()

  // Don't redirect while still loading auth state
  if (loading) {
    return <ProfileLoadingState />
  }

  // Redirect to profile if not complete
  // Don't redirect if already on the profile page
  if (!profileComplete && !location.pathname.endsWith('/profile')) {
    return (
      <Navigate
        to={`/rx/${slug}/profile`}
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  // Profile is complete - render the protected content
  return <>{children}</>
}

/**
 * Loading state shown while checking profile status.
 */
function ProfileLoadingState() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige">
      <div className="flex flex-col items-center gap-4">
        {/* Spinner */}
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-lavender border-t-deep-purple" />
        <p className="text-sm text-charcoal/70">Checking profile...</p>
      </div>
    </div>
  )
}

export default RequireProfile
